import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import {
  TagsService,
  CategoriesService,
  MaterialsService,
  SuppliersService,
  ColorsService,
} from '../api';
import { ProductTag } from '../types/product.types';
import { normalizeTags, removeDuplicateTags } from '../utils/helpers';
import { useAppSelector } from '../redux/hook';
import { selectIsAuthenticated } from '../redux/slices/auth.slice';
import { usePermission } from './usePermission';
import { PRODUCT_PERMISSIONS } from '../constants/permissions.constants';

interface CatalogItem {
  id: number;
  name: string;
}

interface ProductMetadataContextValue {
  tags: ProductTag[];
  productTypes: CatalogItem[];
  productMaterials: CatalogItem[];
  productColors: CatalogItem[];
  productSuppliers: CatalogItem[];
  refreshTags: () => Promise<void>;
  refreshTypes: () => Promise<void>;
  refreshMaterials: () => Promise<void>;
  refreshColors: () => Promise<void>;
  refreshSuppliers: () => Promise<void>;
  isLoading: boolean;
}

const ProductMetadataContext = createContext<ProductMetadataContextValue | null>(null);

const DEFAULT_LIMIT = 200;

async function fetchAllPages<T>(
  fetcher: (page: number, limit: number) => Promise<any>,
): Promise<T[]> {
  const limit = DEFAULT_LIMIT;
  let page = 1;
  let results: T[] = [];
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await fetcher(page, limit);
    const data = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    results = [...results, ...data];
    const metaTotalPages = response?.meta?.totalPages ?? response?.pagination?.totalPages;
    const metaCurrentPage =
      response?.meta?.currentPage ?? response?.pagination?.currentPage ?? page;
    if (metaTotalPages) {
      totalPages = metaTotalPages;
      page = metaCurrentPage + 1;
    } else if (!response?.meta && !response?.pagination) {
      break;
    } else {
      page += 1;
    }
  }

  return results;
}

export const ProductMetadataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tags, setTags] = useState<ProductTag[]>([]);
  const [productTypes, setProductTypes] = useState<CatalogItem[]>([]);
  const [productMaterials, setProductMaterials] = useState<CatalogItem[]>([]);
  const [productColors, setProductColors] = useState<CatalogItem[]>([]);
  const [productSuppliers, setProductSuppliers] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const canViewProducts = usePermission(PRODUCT_PERMISSIONS.VIEW);

  const refreshTags = useCallback(async () => {
    const allTags = await fetchAllPages<ProductTag>(async (page, limit) =>
      TagsService.tagsControllerFindAll(page, limit),
    );
    setTags(removeDuplicateTags(normalizeTags(allTags)));
  }, []);

  const refreshTypes = useCallback(async () => {
    const data = await fetchAllPages<any>((page, limit) =>
      CategoriesService.categoriesControllerFindAll(page, limit),
    );
    setProductTypes(data.map((item: any) => ({ id: item.id, name: item.name })));
  }, []);

  const refreshMaterials = useCallback(async () => {
    const data = await fetchAllPages<any>((page, limit) =>
      MaterialsService.materialsControllerFindAll(page, limit),
    );
    setProductMaterials(data.map((item: any) => ({ id: item.id, name: item.name })));
  }, []);

  const refreshSuppliers = useCallback(async () => {
    const data = await fetchAllPages<any>((page, limit) =>
      SuppliersService.suppliersControllerFindAll(page, limit),
    );
    setProductSuppliers(data.map((item: any) => ({ id: item.id, name: item.name })));
  }, []);

  const refreshColors = useCallback(async () => {
    const data = await fetchAllPages<any>((page, limit) =>
      ColorsService.colorsControllerFindAll(page, limit),
    );
    setProductColors(data.map((item: any) => ({ id: item.id, name: item.name })));
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          refreshTags(),
          refreshTypes(),
          refreshMaterials(),
          refreshColors(),
          refreshSuppliers(),
        ]);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    // Solo intentamos cargar metadata cuando hay sesión activa
    if (isAuthenticated && canViewProducts) {
      loadAll().catch(() => {
        // Evitamos romper la app si la API responde 401 u otro error al iniciar
      });
    } else {
      setIsLoading(false);
    }
    return () => {
      mounted = false;
    };
  }, [
    refreshTags,
    refreshTypes,
    refreshMaterials,
    refreshColors,
    refreshSuppliers,
    isAuthenticated,
    canViewProducts,
  ]);

  const value = useMemo<ProductMetadataContextValue>(
    () => ({
      tags,
      productTypes,
      productMaterials,
      productColors,
      productSuppliers,
      refreshTags,
      refreshTypes,
      refreshMaterials,
      refreshColors,
      refreshSuppliers,
      isLoading,
    }),
    [
      tags,
      productTypes,
      productMaterials,
      productColors,
      productSuppliers,
      refreshTags,
      refreshTypes,
      refreshMaterials,
      refreshColors,
      refreshSuppliers,
      isLoading,
    ],
  );

  return React.createElement(ProductMetadataContext.Provider, { value }, children);
};

export const useProductMetadata = (): ProductMetadataContextValue => {
  const context = useContext(ProductMetadataContext);
  if (!context) {
    throw new Error('useProductMetadata must be used within a ProductMetadataProvider');
  }
  return context;
};
